import type { Network } from '@shared';

import { CHAIN_ID, USER_DATA } from '@common';
import { Web3Provider } from '@ethersproject/providers';
import { ContractType } from '@shared';
import { APIName, Fun } from '@util/api';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLocalStorage } from 'src/hooks';
import { createContainer } from 'unstated-next';
import { useDebouncedCallback } from 'use-debounce';

import { user } from './user';

let handleAccountsChanged: (accounts: string[]) => void;
let handleChainChanged: (newChainId: string) => void;

function useWeb3Modal() {
  const [persistChainId, setPersistChainId] = useLocalStorage<null | number>(
    CHAIN_ID,
    null
  );
  const [address, setAddress] = useState<string>();
  const [chainId, setChainId] = useState<number>(
    persistChainId || Number(process.env.REACT_APP_DEFAULT_CHAINID)
  );
  const [chainList, setChainList] = useState<Network[]>();
  const [provider, setProvider] = useState<Web3Provider>();
  const navigate = useNavigate();
  const {
    isLogin,
    isNoMetamask,
    setIsLogin,
    setUser,
    updateMetamaskStatus,
    userData,
  } = user.useContainer();
  const { t } = useTranslation();
  const location = useLocation();

  const storeDefaultChainId = (newId: number) => {
    setChainId(newId);
    setPersistChainId(newId);
  };

  const getMarketAddress = () => {
    const contracts = chainList?.find(
      (item) => item.chain_id === chainId
    )?.contracts;
    return (
      contracts?.find((item) => item.type === ContractType.MARKETPLACE_721)
        ?.address || '' // using empty string to throw error instead of zero address
    );
  };

  const initData = async (addr: string, pro: Web3Provider) => {
    // Which means calling this function multiple time will not update address and add listener
    if (address !== addr) {
      setAddress(addr);
      const metaMaskProvider: any = pro?.provider;
      metaMaskProvider?.on('chainChanged', handleChainChanged);
      metaMaskProvider?.on('accountsChanged', handleAccountsChanged);
    }

    const _chainId = (await pro.getNetwork()).chainId; // decimal value
    setChainId(_chainId);
    handleChainChanged(_chainId.toString(16));
  };

  const getProvider = async () => {
    let web3: any;
    const { ethereum } = window as any;
    if (typeof ethereum === 'undefined') {
      toast.error(t('toastMessage.metamaskRequired'));
      return false;
    }
    try {
      web3 = await ethereum.request({ method: 'eth_requestAccounts' });
    } catch (error: any) {
      if (error?.message === 'User rejected the request.') {
        toast.error(t('toastMessage.metamaskLoginRequired'));
      }
      return false;
    }
    if (web3?.length === 0) return false;
    return new Web3Provider(ethereum, 'any');
  };

  const connectWeb3Modal = async (
    turnstileResponse: string,
    language: string,
    redirectLink = '/profile'
  ): Promise<boolean> => {
    const _provider = await getProvider();
    if (!_provider) return false;
    setProvider(_provider);

    const _address = await _provider.getSigner().getAddress();
    if (userData != null) {
      initData(_address, _provider);
      return true;
    }

    // Prevent multi login at the same time
    const myNonce = (Math.random() + 1).toString(36).substring(7);
    const serverNonce = await Fun[APIName.POST_GET_NONCE](
      _address.toLowerCase(),
      myNonce
    );

    if (!serverNonce) return false;
    try {
      const signature = await _provider
        .getSigner()
        .signMessage(serverNonce.toString());

      if (!signature) return false;

      const response = await Fun[APIName.POST_CONNECT_WALLET](
        _address.toLowerCase(),
        signature,
        myNonce,
        turnstileResponse,
        language
      );

      if (!response) return false;
      setUser(response);
      setIsLogin(true);

      updateMetamaskStatus(response);
      initData(_address, _provider);
      navigate(redirectLink);
    } catch (error: any) {
      return false;
    }
    return true;
  };

  const disconnectWeb3Modal = async (redirectLink = '/', state?: any) => {
    const metaMaskProvider: any = provider?.provider;
    if (metaMaskProvider) {
      metaMaskProvider.removeListener('accountsChanged', handleAccountsChanged);
      metaMaskProvider.removeListener('chainChanged', handleChainChanged);
      // removeListener seems not working, so use hacky way to remove listener first
      metaMaskProvider._events = {};
      metaMaskProvider._eventsCount = 0;
    }

    await Fun[APIName.LOGOUT]();
    setIsLogin(false);
    setUser(null);
    setAddress(undefined);
    localStorage.removeItem(USER_DATA);
    toast.dismiss('wrong-chain');
    navigate(redirectLink, { state });
  };

  const isValidRequest = async (_chainId: number) => {
    if (!isLogin) {
      navigate('/login', { state: { redirectTo: location.pathname } });
      return false;
    }

    if (isNoMetamask) return true;
    if (!address)
      return connectWeb3Modal('NOT_REQUIRE_TURNSTILE', 'NO_LANGUAGE');

    if (chainId !== _chainId) {
      toast.error(t('toastMessage.supportedChainRequired'));
      return false;
    }
    return true;
  };

  const getAddress = () => {
    if (isNoMetamask) return userData?.user?.wallet?.[0].wallet_address || '';
    return address || '';
  };

  // NOTE: Don't use state here since this function is out of react scope,
  // setState will not have any effect
  handleAccountsChanged = (_accounts: string[]) => {
    disconnectWeb3Modal();
  };

  // NOTE: Don't use state here since this function is out of react scope,
  // setState will not have any effect
  // newChainId is hex value
  handleChainChanged = async (newChainId: string) => {
    const newChainIdDec = parseInt(newChainId, 16);
    setChainId(newChainIdDec);
    const chainIdList = chainList?.map((item) => item.chain_id);
    if (!chainIdList?.includes(newChainIdDec)) {
      toast.error(t('toastMessage.supportedChainRequired'), {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        hideProgressBar: true,
        position: 'bottom-center',
        toastId: 'wrong-chain',
      });
      return;
    }

    toast.dismiss('wrong-chain');
  };

  const getChainName = useMemo(() => {
    return (_chainId?: number): string => {
      if (!_chainId) return '';
      return (
        chainList?.find((item) => item.chain_id === _chainId)?.name ||
        t('header.unsupportedChain')
      );
    };
  }, [chainList]);

  const explorerBaseURL = useMemo(() => {
    return (_chainId: number) =>
      chainList
        ?.find((item) => item.chain_id === _chainId)
        ?.explorers[0]?.url?.replace(/\/+$/, '');
  }, [chainList]);

  const addressExplorerBaseURL = useMemo(() => {
    return (_chainId: number) => `${explorerBaseURL(_chainId)}/address`;
  }, [explorerBaseURL]);

  const transactionExplorerBaseURL = useMemo(() => {
    return (_chainId: number) => `${explorerBaseURL(_chainId)}/tx`;
  }, [explorerBaseURL]);

  const getSupportedChain = async () => {
    const response = await Fun[APIName.GET_LIST_NETWORK]();
    setChainList(response);
  };

  useEffect(() => {
    if (chainList) {
      // Prevent unsupported chain at the first
      if (
        chainList.length > 0 &&
        chainList.find((item) => item.chain_id === chainId) === undefined
      ) {
        storeDefaultChainId(chainList[0].chain_id);
      }
    }
  }, [chainList]);

  useEffect(() => {
    getSupportedChain();
  }, []);

  const connect = useDebouncedCallback(connectWeb3Modal, 1000, {
    leading: true,
    trailing: false,
  });

  const disconnect = useDebouncedCallback(disconnectWeb3Modal, 1000, {
    leading: true,
    trailing: false,
  });

  return {
    addressExplorerBaseURL,
    chainId,
    chainList,
    connect,
    disconnect,
    getAddress,
    getChainName,
    getMarketAddress,
    getProvider,
    isValidRequest,
    provider,
    storeDefaultChainId,
    transactionExplorerBaseURL,
  };
}

export const web3 = createContainer(useWeb3Modal);
